import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, Checkbox, Button, Menu } from "antd";
import { CaretDownFilled } from "@ant-design/icons";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderTableColumnsType from "@/scenes/OrderManagement/enum/OrderTableColumnsType";
import { getMenuDisplayColumns } from "@/scenes/OrderManagement/store/OrderTableStorageStore/utils/orderTableColumns";
import { observer } from "mobx-react-lite";
import { useGlobalStore } from "@/store/hooks";
import DraggableMenuItem from "./DraggableMenuItem";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { PremiumFunctionType, usePremiumValidator } from "@/services/PremiumValidator";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import useLogisticsAgingPredictEffect from "./hooks/useLogisticsAgingPredictEffect";
import useKsLogisticsRecommendEffect from "./hooks/useKsLogisticsRecommendEffect";
import ShopType from "@/enum/ShopType";

function empty(e) {
    e.preventDefault();
}

interface AdditionalColumnsSelectProps {
    shop?: ShopDTO;
    tab: OrderTab;
}

const AdditionalColumnsSelect: React.FC<AdditionalColumnsSelectProps> = function (props) {
    const { shop, tab } = props;
    const orderManagementStore = useOrderManagementStore();
    const {
        orderTableColumns,
        getShopOrderTableColumns,
        toggleOrderTableColumnsVisible,
    } = orderManagementStore.storage;

    const [showMoreOrderMenu, setShowMoreOrderMenu] = useState(false);
    const globalStore = useGlobalStore();

    function handleDropdownOpenChange(open: boolean, info: { source: "trigger" | "menu" }) {
        if (!open && info.source === "menu") return;

        setShowMoreOrderMenu(open);
    }

    const { validate, loading } = usePremiumValidator();

    async function handleOrderTableColumnsChange(checkedValue: OrderTableColumnsType[]) {
        const hasLogisticsAgingPredict = checkedValue.includes(OrderTableColumnsType.LOGISTICS_AGING_PREDICT);

        if (shop && hasLogisticsAgingPredict) {
            const flag = await validate({
                shopId: shop.id,
                type: PremiumFunctionType.LOGISTICS_AGING_PREDICT,
            });

            if (!flag) {
                toggleOrderTableColumnsVisible(
                    shop,
                    checkedValue.filter((val) => val !== OrderTableColumnsType.LOGISTICS_AGING_PREDICT),
                );
                return;
            }
        }

        toggleOrderTableColumnsVisible(shop, checkedValue);
    }

    /**
     * 获取订单列表显示列
     */
    useEffect(() => {
        getShopOrderTableColumns(shop);
    }, [shop?.id, globalStore.companyRoleStore.isRetailer, globalStore.companyRoleStore.isSupplier]);

    const checkedValue = useMemo(() => orderTableColumns.filter(({ visible }) => visible).map(({ type: key }) => key), [
        orderTableColumns,
    ]);

    useLogisticsAgingPredictEffect({ shop, tab, checkedValue });
    useKsLogisticsRecommendEffect({ shop, tab, checkedValue });

    return (
        <Checkbox.Group
            style={{ marginRight: 10, height: 24, display: "flex" }}
            value={checkedValue}
            onChange={handleOrderTableColumnsChange}
        >
            <Dropdown menu={{ items: [] }}
                popupRender={() => {
                    const menuDisplayColumns = getMenuDisplayColumns(shop);

                    return (
                        <Menu
                            id="order-table-columns-menu"
                            onDrop={empty}
                            onDragOver={empty}
                            onDragEnter={empty}
                            items={menuDisplayColumns.map((menuDisplayColumn, index) => ({
                                key: menuDisplayColumn.type || index,
                                className: "order-table-columns-select-menu-item",
                                label: (
                                    <DraggableMenuItem
                                        orderTableColumn={menuDisplayColumn}
                                        shop={shop}
                                        tab={tab}
                                        totalLength={menuDisplayColumns.length}
                                        operableIndex={index}
                                    />
                                ),
                            }))}
                        />
                    );
                }}
                open={showMoreOrderMenu}
                onOpenChange={handleDropdownOpenChange}
                placement="bottomRight"
                autoAdjustOverflow={false}
                trigger={["click"]}
            >
                <Button type="default" size="small" style={{ fontSize: 12 }} loading={loading}>
                    <span>显示更多订单信息</span>
                    <CaretDownFilled style={{ marginLeft: 2 }} />
                    {shop?.type === ShopType.KWAI && tab === OrderTab.PENDING_SHIPMENT ? (
                        <div
                            style={{
                                position: "absolute",
                                right: 0,
                                top: "-12px",
                                width: "106px",
                                height: "15px",
                                background: "red",
                                fontSize: "10px",
                                borderRadius: "2px",
                                padding: "1px",
                                color: "white",
                            }}
                        >
                            快手实时推荐最优快递
                        </div>
                    ) : null}
                </Button>
            </Dropdown>
        </Checkbox.Group>
    );
};

export default observer(AdditionalColumnsSelect);
